import { useCallback, useEffect, useRef, useState } from 'react';
import { RgbaColor, RgbaColorPicker } from 'react-colorful';
import chroma from 'chroma-js';

import { ColorPickerProps } from './color-picker.types';
import OptionalComponent from '@ui/optional-component/optional-component';
import EyeDropper from './eye-dropper/eye-dropper';
import PresetColors from './preset-colors';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';

import './color-picker.css';
import './color-picker-horizontal.css';

const ColorPickerHorizontal = ({
  initialColor,
  value,
  onChange,
  containerClassName,
  pickerClassName,
  style,
  presetColors,
  presetColorsLabel,
  variantKey,
}: ColorPickerProps) => {
  const [color, setColor] = useState<{ hex: string; rgba: RgbaColor }>();
  const [hexInputValue, setHexInputValue] = useState<string>('');

  useEffect(() => {
    setColor(undefined);
    setHexInputValue('');
  }, [variantKey]);

  // handle external color change
  useEffect(() => {
    if (color?.hex) {
      setHexInputValue(color.hex.replace('#', '').substring(0, 6));
    }
  }, [color?.hex]);

  // if initial color changes, reset hex value
  useEffect(() => {
    if (initialColor) {
      setHexInputValue(initialColor.replaceAll('#', '').substring(0, 6));
    }
  }, [initialColor]);

  useEffect(() => {
    if (color) return;

    const isValidColor = chroma.valid(initialColor);

    if (!initialColor || !isValidColor) {
      const startingColor = { r: 50, g: 100, b: 150, a: 1 };
      setColor({
        rgba: startingColor,
        hex: chroma(`rgba(${startingColor.r}, ${startingColor.g}, ${startingColor.b}, ${startingColor.a})`).hex(),
      });
      return;
    }

    const hex = chroma(initialColor).hex();
    const rgba = chroma(initialColor).rgba();
    setColor({
      rgba: {
        r: rgba[0],
        g: rgba[1],
        b: rgba[2],
        a: rgba[3],
      },
      hex: hex,
    });
  }, [color, initialColor]);

  /* if value is used then it's a controlled component and we should update color based on that value */
  useEffect(() => {
    if (value === undefined) return;

    const isValidColor = chroma.valid(value);

    if (!value || !isValidColor) {
      const startingColor = { r: 50, g: 100, b: 150, a: 1 };
      setColor({
        rgba: startingColor,
        hex: chroma(`rgba(${startingColor.r}, ${startingColor.g}, ${startingColor.b}, ${startingColor.a})`).hex(),
      });
      return;
    }

    const hex = chroma(value).hex();
    const rgba = chroma(value).rgba();
    setColor({
      rgba: {
        r: rgba[0],
        g: rgba[1],
        b: rgba[2],
        a: rgba[3],
      },
      hex: hex,
    });
  }, [value]);

  const renderRef = useRef<number>(0);

  const handleColorPickerChange = useCallback(
    (color: RgbaColor) => {
      // do not attempt to update this value more than once per frame
      cancelAnimationFrame(renderRef.current);
      renderRef.current = requestAnimationFrame(() => {
        const rgbaString = `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
        const hex = chroma(rgbaString).hex();
        setColor({
          rgba: color,
          hex,
        });
        onChange?.({
          rgba: rgbaString,
          hex,
          rgb: `rgb(${color.r}, ${color.g}, ${color.b})`,
          opacity: color.a,
        });
      });
    },
    [onChange]
  );

  const handleCustomSelectColor = useCallback(
    (color: string) => {
      const asRgba = chroma(color).rgba();
      handleColorPickerChange({
        r: asRgba[0],
        g: asRgba[1],
        b: asRgba[2],
        a: asRgba[3],
      });
    },
    [handleColorPickerChange]
  );

  const eyeDropperAvailable = typeof window !== 'undefined' && !!window?.EyeDropper;

  const handleHexInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const hexValue = e.target.value;

      const cleanedHex = hexValue.replace(/[^0-9A-Fa-f]/g, '').substring(0, 6);

      setHexInputValue(cleanedHex);

      // only update color if we have a valid hex value
      if (cleanedHex.length === 6) {
        const fullHex = `#${cleanedHex}`;

        if (chroma.valid(fullHex)) {
          handleCustomSelectColor(fullHex);
        }
      }
    },
    [handleCustomSelectColor]
  );

  const handleHexInputBlur = useCallback(() => {
    if (!hexInputValue.trim() || hexInputValue.length !== 6) {
      setHexInputValue(color?.hex?.replace('#', '').substring(0, 6) || '');
    }
  }, [hexInputValue, color?.hex]);

  return color ? (
    <div className={`color-picker-container horizontal ${containerClassName || ''}`}>
      <div className={`rgba-picker-container`}>
        <RgbaColorPicker
          color={color?.rgba}
          onChange={handleColorPickerChange}
          className={`color-picker ${pickerClassName || ''} ${eyeDropperAvailable ? 'eye-dropper-available' : ''}`}
          style={style || {}}
        />

        <OptionalComponent display={eyeDropperAvailable}>
          <div className="color-picker-eye-dropper-container">
            <EyeDropper onSelectColor={handleCustomSelectColor} />
          </div>
        </OptionalComponent>

        <div className="flex-column jc-center ai-center hex-color-input-wrapper flex-1">
          <div className="color-readout">
            <span className="color-readout-label">HEX</span>
            <span className="color-readout-opacity">{Math.floor((color?.rgba.a || 1) * 100)}%</span>
            <InputWithOverlay
              isBorderless
              inputHeight="20px"
              inputWidth="100px"
              inputProps={{
                style: { padding: '0 12px' },
                type: 'text',
                value: hexInputValue,
                onChange: handleHexInputChange,
                onBlur: handleHexInputBlur,
                placeholder: '000000',
                maxLength: 6,
              }}
              renderInnerContent={() => <span className="hash">#</span>}
            />
          </div>
        </div>

        <div
          style={{ backgroundColor: `rgba(${color?.rgba.r}, ${color?.rgba.g}, ${color?.rgba.b}, ${color?.rgba.a})` }}
          className="square-32 radius-4 color-picker-display-block"
        />
      </div>

      <PresetColors
        basic
        colors={presetColors || []}
        onSelectColor={handleCustomSelectColor}
        label={presetColorsLabel}
      />
    </div>
  ) : null;
};

export default ColorPickerHorizontal;
